import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Box, IconButton } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'

import { useSnvListQuery } from 'services/hooks/supplierNameVariants/useSnvListQuery'
import { useSuppliersQuery } from 'services/hooks/supplier/useSuppliersQuery'
import { useSnvServicesQuery } from 'services/hooks/supplierNameVariants/useSnvServicesQuery'
import { useSnvDeleteMutation } from 'services/hooks/supplierNameVariants/useSnvDeleteMutation'
import { NameVariantDTO } from 'declarations'
import {
  DataGrid,
  GridColDef,
  GridPagination,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid'
import DataGridCustomPagination from 'components/dataGridCustomPagination'
import SelectAssociate from './table/selectAssociate'
import TableActions from './table/tableActions'
import { useSnvUpdateMutation } from 'services/hooks/supplierNameVariants/useSnvUpdateMutation'
import FilterBox from './table/filterBox'

const SupplierNameVariants = () => {
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 25,
  })
  const [defaultBulkSupplier, setDefaultBulkSupplier] = useState('')
  const [filters, setFilters] = useState({
    showOnlyUnassignedName: false,
    service: '',
    term: '',
  })
  const {
    data: listData,
    isFetching: isFetchingSnvList,
    refetch: refetchSnvList,
  } = useSnvListQuery({
    pageIndex: paginationModel.page,
    rowsCount: paginationModel.pageSize,
    filterValues: {
      showOnlyUnassignedName: filters.showOnlyUnassignedName,
      service: filters.service,
    },
    searchTerm: filters.term,
  })
  const { data: suppliers } = useSuppliersQuery()
  const { data: services } = useSnvServicesQuery()
  const { mutate: deleteMutation } = useSnvDeleteMutation()
  const { mutate: updateSnvMutation } = useSnvUpdateMutation()

  const columns: GridColDef[] = [
    {
      field: 'name_variant',
      headerName: 'Name Variant',
      flex: 1,
    },
    {
      field: 'service_name',
      headerName: 'Service',
      flex: 1,
    },
    {
      field: 'table_for',
      headerName: 'Supplier',
      renderCell: (params: GridRenderCellParams) =>
        suppliers && (
          <SelectAssociate
            list={suppliers}
            value={params.row.supplier_id}
            uuid={params.row.uuid}
            tableFor='supplier'
          />
        ),
      display: 'flex',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'delete',
      headerName: '',
      renderCell: (params: GridRenderCellParams) => (
        <IconButton aria-label='delete'>
          <DeleteIcon onClick={() => onDeleteRow(params.row)} />
        </IconButton>
      ),
    },
  ]
  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>([])
  const rowCountRef = React.useRef(listData?.total_rows || 0)

  const rowCount = React.useMemo(() => {
    if (listData?.total_rows !== undefined) {
      rowCountRef.current = listData.total_rows
    }
    return rowCountRef.current
  }, [listData?.total_rows])

  const onChangeFilter = (filterObj) => {
    setFilters(filterObj)
    setPaginationModel({
      ...paginationModel,
      page: 0,
    })
  }

  const onDeleteRow = (row: NameVariantDTO) => {
    deleteMutation(
      { uuid: row.uuid },
      {
        onSuccess() {
          refetchSnvList()
          toast.success(
            `${row.name_variant}/${row.service_name} successfully deleted`
          )
        },
      }
    )
  }

  const onUpdateSelected = async (selectedValue) => {
    setDefaultBulkSupplier(selectedValue)

    updateSnvMutation(
      {
        uuids: rowSelectionModel as string[],
        updateBody: {
          supplier_id: selectedValue,
        },
      },
      {
        onSuccess(data, variables, context) {
          refetchSnvList()
        },
      }
    )
  }

  useEffect(() => {
    setDefaultBulkSupplier('')
  }, [rowSelectionModel])

  return (
    <>
      {services && (
        <Box display='flex' alignItems='center' justifyContent='flex-end'>
          <FilterBox
            services={services}
            onChangeFilter={onChangeFilter}
            tableFor='supplier'
          />
        </Box>
      )}
      <Box sx={{ height: 500 }}>
        <DataGrid
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel)
          }}
          rowSelectionModel={rowSelectionModel}
          rows={listData?.supplier_name_variants || []}
          columns={columns} // Define your columns
          rowCount={rowCount}
          paginationMode='server'
          paginationModel={paginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          onPaginationModelChange={setPaginationModel}
          loading={isFetchingSnvList}
          slots={{
            pagination: (props) => (
              <GridPagination
                ActionsComponent={DataGridCustomPagination}
                {...props}
              />
            ),
          }}
          getRowId={(row) => row.uuid}
        />
      </Box>
      {suppliers && (
        <TableActions
          defaultValue={defaultBulkSupplier}
          bulkActionDisabled={rowSelectionModel.length === 0}
          list={suppliers}
          onUpdateSelected={onUpdateSelected}
          tableFor='supplier'
        />
      )}
    </>
  )
}

export default SupplierNameVariants
